<template>
	<div
		v-if="navEnabled"
		ref="footerNav"
		class="footer-nav-mobile"
	>
		<div
			ref="navInner"
			class="footer-nav-mobile__inner"
		>
			<div
				v-for="(item, index) in navItems"
				:key="item.label"
				ref="navItems"
				class="footer-nav-mobile__item"
			>
				<a
					:href="item.href"
					class="footer-nav-mobile__item-link"
					:class="{ 'is-active': item.active }"
					@click="onItemClick(index, $event)"
				>
					<Icon
						:icon-name="item.icon"
						class="footer-nav-mobile__item-icon"
						size="lg"
					/>
					<span class="footer-nav-mobile__item-label">
						{{ item.label }}
					</span>
				</a>
			</div>
			<div
				ref="activeIndicator"
				class="footer-nav-mobile__active-indicator"
				:class="{ 'is-transitioning': shouldTransition }"
				:style="indicatorStyle"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import laroute from '@laroute';
import { mapGetters } from 'vuex';

export default {
	name:       'FooterNavMobile',
	components: {
		Icon,
	},
	data() {
		return {
			activeIndex:      -1,
			clickedIndex:     -1,
			indicatorStyle:   {},
			isInitialLoad:    true,
			shouldTransition: false,
		};
	},
	computed: {
		...mapGetters({
			userInitialized:  'user/isInitialized',
			profile:          'user/profile',
			profileUrl:       'user/profileUrl',
			isMobilePortrait: 'device/isMobilePortrait',
		}),
		navItems() {
			const items = [
				{
					label:  this.$I18n.trans('commons.arena_profile'),
					icon:   'stadium',
					href:   '/',
					active: this.clickedIndex === 0 || (this.clickedIndex === -1 && window.location.pathname === '/'),
				},
			];

			if (this.$arenaConfig.enableVideoFeed) {
				items.push({
					label:  this.$I18n.trans('arena.feed.label'),
					icon:   'feed',
					href:   `${laroute.route('arena.feed')}`,
					active: this.clickedIndex === 1 || (this.clickedIndex === -1 && window.location.pathname.startsWith('/feed')),
				});
			}

			if (this.$arenaConfig.enableMomentsFeed && this.userInitialized && this.profile) {
				const momentsIndex = this.$arenaConfig.enableVideoFeed ? 2 : 1;
				items.push({
					label:  this.$I18n.trans('event.multiformat_editor.title'),
					icon:   'formatconverter',
					href:   `${laroute.route('arena.moments')}`,
					active: this.clickedIndex === momentsIndex || (this.clickedIndex === -1 && window.location.pathname.startsWith('/moments')),
				});
			}

			if (this.userInitialized && this.profile) {
				items.push({
					label:  this.$I18n.trans('commons.profile'),
					icon:   'account',
					href:   this.profileUrl,
					active: this.clickedIndex === (items.length) || (this.clickedIndex === -1 && window.location.pathname.startsWith('/account')),
				});
			}
			return items;
		},
		navEnabled() {
			return this.navItems.length > 1 && this.isMobilePortrait && this.$arenaConfig.enableVideoFeed;
		},
	},
	watch: {
		navItems: {
			handler(newVal, oldVal) {
				// Only update if the number of items changed, not on active state changes
				if (!oldVal || newVal.length !== oldVal.length) {
					this.$nextTick(() => {
						this.updateActiveIndex();
						this.isInitialLoad = true;
						setTimeout(() => {
							this.updateIndicatorPosition();
						}, 50);
					});
				}
			},
			deep: true,
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.clickedIndex = -1;
			this.updateActiveIndex();
			setTimeout(() => {
				this.updateIndicatorPosition(null, false);
			}, 50);
		});
		window.addEventListener('resize', this.handleResize);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
	},
	methods: {
		updateActiveIndex() {
			this.activeIndex = this.navItems.findIndex(item => item.active);
		},
		updateIndicatorPosition(targetIndex = null, forceNoTransition = false) {
			const index = targetIndex !== null ? targetIndex : this.activeIndex;
			if (index === -1 || !this.$refs.navItems || !this.$refs.navItems[index]) {
				if (this.isInitialLoad) {
					this.isInitialLoad = false;
				}
				this.indicatorStyle = { transform: 'translateX(-100%)', width: '0px', transition: 'none' };
				return;
			}

			const itemElement = this.$refs.navItems[index];
			const navInner = this.$refs.navInner;

			if (!itemElement || !navInner) {
				if (this.isInitialLoad) {
					setTimeout(() => this.updateIndicatorPosition(targetIndex, forceNoTransition), 50);
				}
				return;
			}

			const itemRect = itemElement.getBoundingClientRect();
			const navRect = navInner.getBoundingClientRect();

			// Calculate center position for the indicator
			const itemCenter = itemRect.left - navRect.left + (itemRect.width / 2);

			// Calculate indicator width as percentage of total width based on number of items
			const navWidth = navRect.width;
			const itemCount = this.navItems.length;
			const indicatorWidth = (navWidth / itemCount); // 85% of item width for some padding
			const left = (itemCenter - (indicatorWidth / 2)) + 2;

			// Use transition unless it's initial load or explicitly disabled
			this.shouldTransition = !this.isInitialLoad && !forceNoTransition;

			if (this.isInitialLoad) {
				this.isInitialLoad = false;
			}

			this.indicatorStyle = {
				transform: `translateX(${left}px)`,
				width:     `calc(${indicatorWidth}px - 4px)`,
			};
		},
		onItemClick(index, event) {
			event.preventDefault();
			this.clickedIndex = index;
			this.updateActiveIndex();

			// Ensure transition is enabled
			this.$nextTick(() => {
				this.updateIndicatorPosition(null, false);
			});

			// Navigate after animation
			setTimeout(() => {
				window.location.href = this.navItems[index].href;
			}, 550);
		},
		handleResize() {
			this.$nextTick(() => {
				this.updateIndicatorPosition(null, true);
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.footer-nav-mobile {
	--active-color: var(--color-white);
	--inactive-color: var(--color-grey-300);
	--bg-color: var(--color-grey-900);
	--indicator-bg-color: var(--color-grey-800);

	@include z-index(arena-sticky-mobile-footer-nav);
	position: sticky;
	bottom: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: $sp3 $sp3;

	body.-t-light & { // stylelint-disable-line
		--active-color: var(--color-black);
		--inactive-color: var(--color-grey-500);
		--bg-color: var(--color-grey-950);
		--indicator-bg-color: var(--color-grey-850);
	}
}

.footer-nav-mobile__inner {
	display: flex;
	align-items: center;
	justify-content: space-evenly;
	background-color: var(--bg-color);
	border-radius: $border-radius--full;
	overflow: hidden;
	position: relative;
	width: 100%;
	max-width: 480px;
}

.footer-nav-mobile__item {
	display: flex;
	flex: 1;
	position: relative;
	z-index: 2;
}

.footer-nav-mobile__item-link {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: $sp2;
	padding: $sp3-5 $sp3;
	width: 100%;
	color: var(--inactive-color);
	transition: color 0.3s ease;
	position: relative;
	z-index: 3; // stylelint-disable-line

	&.is-active {
		color: var(--active-color);
	}
}

.footer-nav-mobile__item-icon {
	flex-shrink: 0;
}

.footer-nav-mobile__item-label {
	font-size: 10px; // stylelint-disable-line
	font-weight: 500; // stylelint-disable-line
	text-align: center;
}

.footer-nav-mobile__active-indicator {
	position: absolute;
	top: 2px;
	left: 0;
	height: calc(100% - 4px);
	background-color: var(--indicator-bg-color);
	border-radius: $border-radius--full;
	z-index: 1;
	pointer-events: none;
	transition: none;

	&.is-transitioning {
		transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}
}
</style>
